import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useApi } from "@/lib/useApi";
import { dateLabel } from "@/lib/format";
import { Card, EmptyState, ErrorState, ListSkeleton, PageTitle } from "@/components/common";
import { AddTenantSheet } from "@/components/AddTenantSheet";
import { BackLink } from "@/pages/More";

const CLS: Record<string, string> = {
  pending: "bg-amber-50 text-amber-800 border-amber-200", accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-rose-50 text-rose-700 border-rose-200", cancelled: "bg-slate-50 text-slate-500 border-slate-200",
};

export default function JoinRequests() {
  const { property, reloadProperties } = useAuth();
  const { data, loading, error, reload } = useApi<any[]>(property ? `/properties/${property.id}/join-requests` : null);
  const [accepting, setAccepting] = useState<any>(null);
  const decline = async (id: string) => {
    if (!window.confirm("Decline this request?")) return;
    try { await api.post(`/join-requests/${id}/decline`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <BackLink />
      <PageTitle title="Join requests" sub={property?.name} />
      {loading && !data ? <ListSkeleton rows={3} /> : error ? <ErrorState message={error} onRetry={reload} /> : !data.length ? (
        <EmptyState testId="join-requests-empty" icon={UserPlus} title="No join requests yet" text="Publish your PG listing in Settings so residents can find you and request to join." />
      ) : (
        <div className="space-y-2.5 pg-stagger">
          {data.map((j) => (
            <Card key={j.id} className="p-4" data-testid={`owner-join-${j.id}`}>
              <div className="flex justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold">{j.name}</p>
                  <p className="text-xs text-slate-500">+91 {j.phone} · {j.email}</p>
                  {j.room_name && <p data-testid={`owner-join-room-${j.id}`} className="mt-1 mb-0.5 w-fit text-xs font-bold rounded-full bg-[#e8f1fb] text-[#131e2e] px-2.5 py-1">Wants Room {j.room_name}{j.room_floor ? ` · Floor ${j.room_floor}` : ""}</p>}
                  <p className="text-xs text-slate-500">Move-in {dateLabel(j.move_in_date)}{j.sharing ? ` · ${j.sharing === 1 ? "Single" : j.sharing + "-sharing"}` : ""}</p>
                </div>
                <span className={`h-fit text-xs font-semibold rounded-full border px-2.5 py-1 capitalize ${CLS[j.status]}`}>{j.status}</span>
              </div>
              {j.message && <p className="text-sm text-slate-600 mt-2">"{j.message}"</p>}
              {j.status === "pending" && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Button data-testid={`owner-join-decline-${j.id}`} variant="outline" onClick={() => decline(j.id)} className="h-11 rounded-xl text-rose-600 border-rose-200">Decline</Button>
                  <Button data-testid={`owner-join-accept-${j.id}`} onClick={() => setAccepting(j)} className="h-11 rounded-xl bg-emerald-700 hover:bg-emerald-800">Accept & assign bed</Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      <AddTenantSheet open={!!accepting} bedId={accepting?.room_free_bed_id || undefined} onClose={() => setAccepting(null)} prefill={accepting ? { name: accepting.name, phone: accepting.phone, email: accepting.email, move_in_date: accepting.move_in_date, join_request_id: accepting.id } : undefined}
        onSaved={() => { reload(); reloadProperties(); }} />
    </div>
  );
}
